import {
  getEWSMailMIME,
  classifyEwsMimeFailure,
  fetchGraphMime,
  mapEwsResponseCode,
  parseResponse,
  useReportMail,
  EwsMimeError,
  type EwsMimeDiagnosis,
  type EwsMimeFailureCause,
  diagnoseEwsMimeFailure,
  useIsV2,
} from '@data';
import { useToastsContext } from '@utils';
import {
  ActionTaken,
  handleError,
  getOriginalMailId,
  isRequestTimeout,
  logError,
  maskEmail,
  NoMailItemError,
  ReportAction,
  type ReportResponseType,
  ReportType,
  waitBeforePostReportAction,
} from '@utils-global';
import { useState } from 'react';
import { TIMING_METRICS } from '@constants';
import { useTimingMetric } from './useTimingMetric.hook';
import { handleReportResultAction, resolveActionMailId } from '../pages/report/api';
import {
  selectReportResultScreen,
  type ReportResultScreen,
} from '../pages/report/selectReportResultScreen';

const EWS_MIME_TOAST_BY_CAUSE: Record<EwsMimeFailureCause, string> = {
  tooLarge: 'errors.mimeTooLarge',
  itemNotFound: 'errors.emailWasDeleted',
  accessDenied: 'errors.mimeAccessDenied',
  serverBusy: 'errors.mimeServerBusy',
  mimeGeneration: 'errors.mimeGenerationFailed',
  sharedFolderUnsupported: 'errors.sharedMailboxNotSupported',
  offline: 'errors.offline',
  probeFailed: 'errors.mimeProbeFailed',
  ewsError: 'errors.mimeEwsError',
  unknown: 'errors.mimeUnknown',
};

type UseReportMailFlowParams = {
  mailId: string | undefined;
  mailboxEmail: string;
  isSharedMailbox: boolean;
  originalUserEmail: string;
  actionsTaken: ActionTaken[];
  isOnPremises: boolean;
  reportType: ReportType;
  keepEmail?: boolean;
};

export const useReportMailFlow = ({
  mailId,
  mailboxEmail,
  isSharedMailbox,
  originalUserEmail,
  actionsTaken,
  isOnPremises,
  reportType,
  keepEmail,
}: UseReportMailFlowParams) => {
  const { showToast } = useToastsContext();
  const { mutate: reportMail, data: reportResult, reset: resetMutation } = useReportMail();
  const isV2 = useIsV2();
  const { startTimingMetric, stopTimingMetric } = useTimingMetric();
  const [isLoading, setIsLoading] = useState(false);

  // One decision site for which screen the report lands on, so the rendered result and the
  // timing metric can never disagree about it.
  const resolveScreen = (type: ReportResponseType): ReportResultScreen =>
    selectReportResultScreen({
      isV2,
      reportType: type,
      actionsTaken,
    });

  const reportMimeFailure = (diagnosis: EwsMimeDiagnosis, context: Record<string, unknown>) => {
    const { cause } = diagnosis;
    const error = new EwsMimeError(`EWS getMailMIME failed (cause: ${cause})`, cause, {
      ...diagnosis,
      ...context,
    });

    showToast(EWS_MIME_TOAST_BY_CAUSE[cause] ?? 'errors.mime');

    return error;
  };

  const getMIMECloud = () => {
    return fetchGraphMime({ mailId, mailboxEmail, isSharedMailbox }, showToast);
  };

  // On-Premises (EWS) MIME fetching
  const getMIMEOnPremises = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      const originalMailId = getOriginalMailId();
      if (!originalMailId) {
        reject(new NoMailItemError('Could not retrieve mail identifier'));
        return;
      }

      getEWSMailMIME(originalMailId, async (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          const diagnosis = await classifyEwsMimeFailure(
            asyncResult.error,
            originalMailId,
            isSharedMailbox,
          );

          reject(
            reportMimeFailure(diagnosis, {
              isSharedMailbox,
              mailboxEmail: maskEmail(mailboxEmail),
              itemId: originalMailId,
              code: asyncResult.error?.code,
            }),
          );
          return;
        }

        const [mimeContent, responseCode, messageText] = parseResponse(asyncResult.value, [
          'MimeContent',
          'ResponseCode',
          'MessageText',
        ]);

        // EWS can return a transport-level success carrying an operation-level error
        // (ResponseClass="Error"). Surface the named cause instead of a misleading
        // parse failure, routing it through the same classifier as the 9020 path.
        if (!mimeContent && responseCode && responseCode !== 'NoError') {
          reject(
            reportMimeFailure(
              {
                cause: mapEwsResponseCode(responseCode),
                responseCode,
                messageText: messageText ?? undefined,
              },
              { itemId: originalMailId },
            ),
          );
          return;
        }

        // The body carries neither MimeContent nor an operation-level error code to explain it,
        // in practice the body comes back empty/blank. Re-issue a probe which should return a
        // diagnosis.
        if (!mimeContent) {
          const diagnosis = await diagnoseEwsMimeFailure(originalMailId);
          reject(
            reportMimeFailure(diagnosis, {
              resultLength: asyncResult.value?.length ?? 0,
              itemId: originalMailId,
            }),
          );
          return;
        }

        // EWS request returns mime content as base64 encoded string
        // we need to decode it to get the actual content
        const decodedMimeContent = atob(mimeContent);

        if (!decodedMimeContent) {
          showToast('errors.decodeMime');
          reject(new Error('Failed to decode MIME content'));
          return;
        }

        resolve(decodedMimeContent);
      });
    });
  };

  const handleReport = async () => {
    const isPhishingReport = reportType === ReportType.PHISHING;

    if (isPhishingReport) {
      startTimingMetric(TIMING_METRICS.REPORT_PHISHING_SUBMIT_DURATION);
    }

    try {
      setIsLoading(true);

      const mimeContent = isOnPremises ? await getMIMEOnPremises() : await getMIMECloud();
      if (mimeContent === undefined) {
        setIsLoading(false);
        return;
      }

      // Resolve the post-report action id now, while the item is still in place,
      // so a later relocation can't invalidate it before the move/delete runs.
      const { mailId: actionMailId, mailIdKind } =
        (await resolveActionMailId({
          isOnPremises,
          mailId,
          userEmail: mailboxEmail,
          isSharedMailbox,
        })) ?? {};

      reportMail(
        {
          email: mimeContent,
          // Phished API needs to know who sent the request
          reporterEmailAddress: isSharedMailbox ? originalUserEmail || mailboxEmail : mailboxEmail,
          action_taken: actionsTaken,
          report_option: reportType,
          ...(keepEmail !== undefined && { keep_email: keepEmail }),
        },
        {
          onSuccess: async ({ data: response }) => {
            // The flip below is what swaps the loading screen for the result screen, so this
            // is where submitting ends for the user. Recorded on this path only: a
            // failed report never reaches a result screen, and timing it would blend
            // network-timeout durations into a "how long does the user wait" number.
            const screen = resolveScreen(response.Type);

            setIsLoading(false);
            showToast('notifications.reportSent', { intent: 'success' });

            if (isPhishingReport) {
              stopTimingMetric(TIMING_METRICS.REPORT_PHISHING_SUBMIT_DURATION, { screen });
            }

            if (response.Action !== ReportAction.NOTHING) {
              const willBeMovedOrDeletedToast =
                response.Action === ReportAction.DELETE
                  ? 'notifications.willBeDeleted'
                  : 'notifications.willBeMovedToSpam';

              showToast(willBeMovedOrDeletedToast, { intent: 'info' });
            }

            await waitBeforePostReportAction();

            const outcome = await handleReportResultAction(
              response.Action,
              actionMailId,
              mailboxEmail,
              showToast,
              isSharedMailbox,
              {
                mailIdKind,
                reportResponseType: response.Type,
              },
            );

            if (isPhishingReport) {
              stopTimingMetric(TIMING_METRICS.REPORT_DURATION, {
                screen,
                action: ReportAction[response.Action],
                outcome,
                reportType: ReportType.PHISHING,
              });
            }
          },
          onError: (error) => {
            setIsLoading(false);
            showToast(isRequestTimeout(error) ? 'errors.reportTimeout' : 'errors.failedReport');
          },
        },
      );
    } catch (error) {
      setIsLoading(false);
      if (error instanceof EwsMimeError) {
        // Only report the causes we don't explicitly handle yet.
        if (error.cause === 'unknown' || error.cause === 'ewsError') {
          handleError(error);
        } else {
          logError('EWS getMailMIME failed', { error });
        }

        return;
      }

      if (error instanceof NoMailItemError) {
        logError('Could not retrieve mail identifier', { error });
        showToast('errors.noEmailItem');
        return;
      }

      handleError(error);
    }
  };

  const resetReport = () => {
    resetMutation();
  };

  const report = reportResult?.data;

  return {
    isLoading,
    report,
    screen: report ? resolveScreen(report.Type) : undefined,
    handleReport,
    resetReport,
  };
};
